import { getSupportedCommunicationTestingControlTemplates } from "./supportedTestingControls";

type Templates = readonly string[];
type Loose = Record<string, any>;

const parseRowsInput = (rows: unknown): Loose[] => {
  if (Array.isArray(rows)) {
    return rows;
  }

  if (typeof rows === "string") {
    try {
      const parsed = JSON.parse(rows);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  return [];
};

export const hasSupportedCommunicationTestingControl = (
  rows: unknown,
  supportedControlTemplates: Templates = getSupportedCommunicationTestingControlTemplates()
): boolean => {
  const parsedRows = parseRowsInput(rows);

  if (parsedRows.length === 0) {
    return false;
  }

  const supportedTemplates = new Set(
    supportedControlTemplates.map((template) => String(template))
  );

  for (const row of parsedRows) {
    const columns = Array.isArray(row?.Columns)
      ? row.Columns
      : Array.isArray(row?.columns)
        ? row.columns
        : [];

    for (const column of columns) {
      const controls = Array.isArray(column?.Controls)
        ? column.Controls
        : Array.isArray(column?.controls)
          ? column.controls
          : [];

      for (const control of controls) {
        const template = control?.Template ?? control?.template;

        if (template && supportedTemplates.has(String(template))) {
          return true;
        }
      }
    }
  }

  return false;
};

const decodeTemplateData = (templateData: unknown): string => {
  if (!templateData || typeof templateData !== "string") {
    return "";
  }

  try {
    if (typeof atob === "function") {
      return decodeURIComponent(atob(templateData));
    }
  } catch {
    // continue to Buffer fallback
  }

  try {
    if (typeof Buffer !== "undefined") {
      return decodeURIComponent(
        Buffer.from(templateData, "base64").toString("utf8")
      );
    }
  } catch {
    return "";
  }

  return "";
};

export const hasSupportedCommunicationTestingControlInTemplateData = (
  templateData: unknown,
  supportedControlTemplates: Templates = getSupportedCommunicationTestingControlTemplates()
): boolean => {
  const decoded = decodeTemplateData(templateData);

  if (!decoded) {
    return false;
  }

  return supportedControlTemplates.some((template) =>
    decoded.includes(`"${String(template)}"`)
  );
};

export const hasSupportedCommunicationTestingControlInDesignerData = (
  designerData: unknown,
  supportedControlTemplates: Templates = getSupportedCommunicationTestingControlTemplates()
): boolean => {
  if (!designerData || typeof designerData !== "string") {
    return false;
  }

  const supportedTemplates = supportedControlTemplates.map(
    (template) => `"${String(template)}"`
  );

  if (supportedTemplates.some((template) => designerData.includes(template))) {
    return true;
  }

  try {
    const decoded = decodeURIComponent(designerData);

    return supportedTemplates.some((template) => decoded.includes(template));
  } catch {
    return false;
  }
};

export const hasSupportedCommunicationTestingControlInRecord = (
  record: Loose | null | undefined,
  supportedControlTemplates?: Templates
): boolean =>
  hasSupportedCommunicationTestingControl(
    record?.rows ?? record?.Rows,
    supportedControlTemplates
  ) ||
  hasSupportedCommunicationTestingControlInTemplateData(
    record?.templateData ??
      record?.TemplateData ??
      record?.templateDataAnnotation?.Base64String,
    supportedControlTemplates
  ) ||
  hasSupportedCommunicationTestingControlInDesignerData(
    record?.g4c_designerdata ?? record?.g4c_DesignerData,
    supportedControlTemplates
  );

export const hasSupportedCommunicationTestingControlInState =
  hasSupportedCommunicationTestingControlInRecord;
